import { useLayoutEffect, useMemo, useRef, useState } from 'react'

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type SortingState,
  type Table,
} from '@tanstack/react-table'
import type { SortDescriptor } from 'react-aria-components/Table'

import {
  DEFAULT_PAGE_SIZE,
  type DataTableColumnDef,
  type DataTablePaginationConfig,
  type DataTableQuery,
  type DataTableServerPagination,
  type DataTableSort,
} from './data-table-types'

export function toSortingState(
  sort: DataTableSort | null,
): SortingState {
  return sort
    ? [
        {
          id: sort.columnId,
          desc: sort.direction === 'descending',
        },
      ]
    : []
}

export function toDataTableSort(
  sorting: SortingState,
): DataTableSort | null {
  /* DataTable v2 is single-column sort; extra entries are intentionally dropped. */
  const first = sorting[0]

  return first
    ? {
        columnId: first.id,
        direction: first.desc ? 'descending' : 'ascending',
      }
    : null
}

export function toSortDescriptor(
  sort: DataTableSort | null,
): SortDescriptor | undefined {
  return sort
    ? {
        column: sort.columnId,
        direction: sort.direction,
      }
    : undefined
}

export function fromSortDescriptor(
  descriptor: SortDescriptor,
): DataTableSort {
  return {
    columnId: String(descriptor.column),
    direction: descriptor.direction ?? 'ascending',
  }
}

export interface UseDataTableArgs<TData> {
  columns: readonly DataTableColumnDef<TData>[]
  data: readonly TData[]
  getRowId: (row: TData) => string
  pagination?: DataTablePaginationConfig
  search?: boolean
}

export interface UseDataTableResult<TData> {
  table: Table<TData>

  sortDescriptor: SortDescriptor | undefined
  onSortChange: (descriptor: SortDescriptor) => void

  searchValue: string
  onSearchChange: (value: string) => void

  pageIndex: number
  pageSize: number
  pageCount: number
  totalRowCount: number
  setPageIndex: (pageIndex: number) => void
  setPageSize: (pageSize: number) => void
}

export function useDataTable<TData>({
  columns,
  data,
  getRowId,
  pagination,
  search = false,
}: UseDataTableArgs<TData>): UseDataTableResult<TData> {
  const server: DataTableServerPagination | undefined =
    pagination?.mode === 'server' ? pagination : undefined

  const [clientSort, setClientSort] =
    useState<DataTableSort | null>(null)

  const [clientSearch, setClientSearch] = useState('')

  const [clientPagination, setClientPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize:
        pagination?.mode === 'client'
          ? pagination.initialPageSize ?? DEFAULT_PAGE_SIZE
          : DEFAULT_PAGE_SIZE,
    })

  const sort = server ? server.query.sorting : clientSort

  const searchValue = server
    ? server.query.search
    : clientSearch

  const paginationState: PaginationState = server
    ? {
        pageIndex: server.query.pageIndex,
        pageSize: server.query.pageSize,
      }
    : clientPagination

  /*
   * emitQuery may be invoked from closures captured in earlier
   * renders (for example the toolbar's debounce timeout). The ref
   * always points at the latest server config so late emissions
   * merge into the current query instead of reverting it.
   */
  const serverRef = useRef(server)

  useLayoutEffect(() => {
    serverRef.current = server
  })

  function emitQuery(
    partial: Partial<DataTableQuery>,
  ): void {
    const currentServer = serverRef.current

    currentServer?.onQueryChange({
      ...currentServer.query,
      ...partial,
    })
  }

  const isServer = server !== undefined

  const sortingState = useMemo(
    () => toSortingState(sort),
    [sort],
  )

  const table = useReactTable<TData>({
    columns: columns as ColumnDef<TData, any>[],
    data: data as TData[],
    getRowId,

    state: {
      sorting: sortingState,
      globalFilter: searchValue,
      pagination: paginationState,
    },

    getCoreRowModel: getCoreRowModel(),

    getSortedRowModel: isServer
      ? undefined
      : getSortedRowModel(),

    getFilteredRowModel:
      isServer || !search ? undefined : getFilteredRowModel(),

    getPaginationRowModel:
      pagination && !isServer
        ? getPaginationRowModel()
        : undefined,

    manualSorting: isServer,
    manualFiltering: isServer || !search,
    manualPagination: isServer || !pagination,

    globalFilterFn: 'includesString',

    rowCount: server?.rowCount,
  })

  function onSortChange(descriptor: SortDescriptor): void {
    const nextSort = fromSortDescriptor(descriptor)

    if (server) {
      emitQuery({ sorting: nextSort, pageIndex: 0 })
    } else {
      setClientSort(nextSort)

      setClientPagination((current) => ({
        ...current,
        pageIndex: 0,
      }))
    }
  }

  function onSearchChange(value: string): void {
    if (server) {
      emitQuery({ search: value, pageIndex: 0 })
    } else {
      setClientSearch(value)

      setClientPagination((current) => ({
        ...current,
        pageIndex: 0,
      }))
    }
  }

  function setPageIndex(pageIndex: number): void {
    if (server) {
      emitQuery({ pageIndex })
    } else {
      setClientPagination((current) => ({
        ...current,
        pageIndex,
      }))
    }
  }

  function setPageSize(pageSize: number): void {
    if (server) {
      emitQuery({ pageSize, pageIndex: 0 })
    } else {
      setClientPagination({ pageIndex: 0, pageSize })
    }
  }

  const totalRowCount = server
    ? server.rowCount
    : table.getPrePaginationRowModel().rows.length

  const pageCount = server
    ? Math.max(
        1,
        Math.ceil(server.rowCount / paginationState.pageSize),
      )
    : pagination
      ? Math.max(1, table.getPageCount())
      : 1

  return {
    table,
    sortDescriptor: toSortDescriptor(sort),
    onSortChange,
    searchValue,
    onSearchChange,
    pageIndex: paginationState.pageIndex,
    pageSize: paginationState.pageSize,
    pageCount,
    totalRowCount,
    setPageIndex,
    setPageSize,
  }
}
